import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSchool } from '@/contexts/SchoolContext';
import { useAuth } from '@/hooks';
import { workflowTemplateService } from '@/services/workflow/WorkflowTemplateService';

const CACHE_PREFIX = 'swan_cached_workflow_templates_';
const readCache = (schoolId) => {
  if (!schoolId) return null;
  try { return JSON.parse(sessionStorage.getItem(CACHE_PREFIX + schoolId)) ?? null; }
  catch { return null; }
};
const writeCache = (schoolId, value) => {
  if (!schoolId) return;
  try { sessionStorage.setItem(CACHE_PREFIX + schoolId, JSON.stringify(value)); }
  catch { /* quota */ }
};

/**
 * useWorkflowTemplates - Fetches and manages workflow templates for the current school.
 */
export function useWorkflowTemplates() {
  const { schoolId } = useSchool();
  const { user } = useAuth();

  const [templates, setTemplates] = useState(() => readCache(schoolId) || []);
  const [loading, setLoading] = useState(() => readCache(schoolId) == null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active'); // 'active' | 'inactive' | 'all'

  const fetchTemplates = useCallback(async (force = false) => {
    if (!schoolId) {
      setTemplates([]);
      setLoading(false);
      return;
    }

    setError(null);

    try {
      const result = await workflowTemplateService.getTemplates(schoolId, force);
      setTemplates(result);
      writeCache(schoolId, result);
    } catch (err) {
      console.error('[useWorkflowTemplates] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    const cached = readCache(schoolId);
    if (cached != null) {
      setTemplates(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    fetchTemplates();
  }, [fetchTemplates, schoolId]);

  const refetch = useCallback(() => fetchTemplates(true), [fetchTemplates]);

  const filteredTemplates = useMemo(() => {
    switch (filter) {
      case 'active': return templates.filter(t => t.active);
      case 'inactive': return templates.filter(t => !t.active);
      default: return templates;
    }
  }, [templates, filter]);

  const stats = useMemo(() => ({
    total: templates.length,
    active: templates.filter(t => t.active).length,
    inactive: templates.filter(t => !t.active).length,
  }), [templates]);

  const createTemplate = useCallback(async (templateData) => {
    if (!schoolId || !user?.uid) return;
    const result = await workflowTemplateService.createTemplate(schoolId, templateData, user.uid);
    await fetchTemplates(true);
    return result;
  }, [schoolId, user?.uid, fetchTemplates]);

  const updateTemplate = useCallback(async (templateId, updates) => {
    if (!schoolId) return;
    await workflowTemplateService.updateTemplate(schoolId, templateId, updates);
    await fetchTemplates(true);
  }, [schoolId, fetchTemplates]);

  const deleteTemplate = useCallback(async (templateId) => {
    if (!schoolId) return;
    await workflowTemplateService.deleteTemplate(schoolId, templateId);
    await fetchTemplates(true);
  }, [schoolId, fetchTemplates]);

  const toggleActive = useCallback(async (templateId, active) => {
    if (!schoolId) return;
    await workflowTemplateService.toggleActive(schoolId, templateId, active);
    await fetchTemplates(true);
  }, [schoolId, fetchTemplates]);

  const getTemplate = useCallback(async (templateId) => {
    if (!schoolId) return null;
    return workflowTemplateService.getTemplate(schoolId, templateId);
  }, [schoolId]);

  return {
    templates: filteredTemplates,
    allTemplates: templates,
    loading,
    error,
    filter,
    setFilter,
    stats,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    toggleActive,
    getTemplate,
    refetch,
  };
}

export default useWorkflowTemplates;
